import { ApolloClient, from, HttpLink, split } from '@apollo/client'
import type { TypePolicies } from '@apollo/client/cache'
import { InMemoryCache } from '@apollo/client/cache'
import { RetryLink } from '@apollo/client/link/retry'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition, mergeDeep } from '@apollo/client/utilities'
import { getJwtString } from '@backoffice-frontend/authentication'
import {
  fetchWithAuthHeaders,
  GraphQLEndpoint,
  SubscriptionGatewayEndpoint,
} from '@backoffice-frontend/common'
import { apolloVersion } from '@backoffice-frontend/environment'
import { FleetMaintenanceStore } from '@backoffice-frontend/fleet-maintenance'
import { introspectionResult } from '@backoffice-frontend/graphql'
import { MoiaHeaderSearchTypePolicies } from '@backoffice-frontend/patterns'
import {
  MoiaRegionTypePolicies,
  MoiaSelectedFleetsTypePolicies,
  MoiaSelectedHubTypePolicies,
  MoiaServiceAreaTypePolicies,
} from '@backoffice-frontend/service-area-picker'
import { StopNetworkStore } from '@backoffice-frontend/stop-network'
import { createClient } from 'graphql-ws'

export const getApolloClient = (appName: string, policies: TypePolicies[]) => {
  const TypePolicies = {
    ...mergeDeep(
      MoiaHeaderSearchTypePolicies,
      MoiaServiceAreaTypePolicies,
      MoiaRegionTypePolicies,
      MoiaSelectedFleetsTypePolicies,
      MoiaSelectedHubTypePolicies,
      StopNetworkStore,
      FleetMaintenanceStore,
      ...policies,
    ),
    ShiftState: {
      keyFields: ['shiftId'],
    },
    EmployeeNote: {
      keyFields: ['noteId', 'employeeId'],
    },
    ServiceArea: {
      fields: {
        locationAttributes: {
          merge: true,
        },
      },
    },
    Vehicle: {
      fields: {
        latestLocation: {
          merge: true,
        },
        fccCurrentAdAlerts: {
          merge: false,
        },
        fccUpcomingWaypoints: {
          merge: false,
        },
      },
    },
  }

  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 5000,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: error => !!error,
    },
  })

  const httpLink = new HttpLink({
    uri: GraphQLEndpoint,
    fetch: (url: URL | RequestInfo, options?: RequestInit) =>
      fetchWithAuthHeaders(url.toString(), {
        ...options,
        addTraceIdHeader: true,
      }),
  })

  const wsLink = new GraphQLWsLink(
    createClient({
      url: SubscriptionGatewayEndpoint,
      connectionParams: async () => ({
        authorization: await getJwtString(),
      }),
      retryAttempts: 10,
      lazy: true,
    }),
  )

  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: introspectionResult.possibleTypes,
      typePolicies: TypePolicies,
      // Note(Filip): the "resultCacheMaxSize" prop causes heavy performance regression in the Stop Network
    }),
    connectToDevTools: true,
    name: appName,
    version: apolloVersion,
    link: from([
      retryLink,
      split(({ query }) => {
        const definition = getMainDefinition(query)

        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        )
      }, wsLink),
      httpLink,
    ]),
  })
}
